import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Store,
  Package,
  CreditCard,
  Rocket,
  ArrowRight,
  ArrowLeft,
  Upload,
  Sparkles,
  Loader2,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Power,
  Check,
  X,
  Image as ImageIcon,
  MapPin,
  Palette,
  Lock
} from 'lucide-react';
import { useToast } from '../../components/ui/Toast';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import axios from '../../axiosConfig';
import { getPlatformDomainName } from '../../config/platformConfig';
import './NewOnboardingWizard.css';

// ─── Helpers ───────────────────────────────────────────────────────────

/**
 * Generate a URL-safe slug from a business name.
 * Lowercase, alphanumeric + hyphens only, no leading/trailing hyphens.
 */
const slugFromName = (name) => {
  if (!name) return '';
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 50);
};

/**
 * Sentinel for "no premium theme — use the built-in default look".
 * Lives in the same selectedThemeId slot as marketplace theme ids so
 * the grid can render a single uniform list of tiles. Activation
 * (POST /themes/:id/activate) skips this value — the absence of a
 * premium ThemeConfig on the store IS the default state.
 */
const DEFAULT_THEME_ID = 'default';

const DEFAULT_THEME = {
  id: DEFAULT_THEME_ID,
  name: 'Default theme',
  isPremium: false,
  price: 0,
  industry: null,
  previewImages: [],
  isBuiltIn: true
};

/**
 * Map onboarding's category options to the StorefrontTheme.industry enum.
 * Returns null when there's no clean match — caller falls back to showing
 * all free themes without industry priority. Adding new mappings here is
 * preferable to broadening one (e.g., dumping everything into FMCG) because
 * mismatched industry hints actually hurt the user (irrelevant themes float).
 */
const categoryToIndustry = (category) => {
  switch (category) {
    case 'fashion':    return 'FASHION';
    case 'electronics': return 'TECH';
    case 'food':       return 'RESTAURANT';
    case 'toys':       return 'KIDS';
    case 'auto':       return 'AUTOS';
    case 'realestate': return 'REAL_ESTATE';
    // beauty / home / sports / books / other → no clean industry match
    default:           return null;
  }
};

/**
 * Heuristic: does this slug look like gibberish?
 * Trips when length >= 6 and vowel ratio < 15% (English text averages ~38%).
 * Short strings (3-5 chars) are spared since they could be valid abbreviations.
 */
const looksLikeGibberish = (slug) => {
  if (!slug || slug.length < 6) return false;
  const letters = slug.toLowerCase().replace(/[^a-z]/g, '');
  if (letters.length < 4) return false;
  const vowels = (letters.match(/[aeiou]/g) || []).length;
  const ratio = vowels / letters.length;
  return ratio < 0.15;
};

const NewOnboardingWizard = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { isAuthenticated, loading: authLoading, user, logout } = useSecureAuth();
  const [loggingOut, setLoggingOut] = useState(false);

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [generatingProductDescription, setGeneratingProductDescription] = useState(false);
  const [generatingProductImage, setGeneratingProductImage] = useState(false);
  const [generatingLogo, setGeneratingLogo] = useState(false);

  const platformDomain = getPlatformDomainName();

  // Slug state
  const [slugCheckLoading, setSlugCheckLoading] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState(null); // null | true | false
  const [slugError, setSlugError] = useState('');
  const slugCheckTimeoutRef = useRef(null);
  // Track whether the slug was auto-filled from the business name (vs. user typed it)
  // so we know when to keep auto-syncing as the name changes.
  const slugAutoSyncRef = useRef(true);

  // Form data
  // storeLogo holds a File while the merchant is in the wizard; the backend
  // accepts it under the `storeLogo` multipart field. Address fields here
  // populate Location (Main warehouse) AND MerchantKYC.businessAddress —
  // KYCWizard pre-fills from MerchantKYC so merchants don't re-enter them.
  const [storeData, setStoreData] = useState({
    storeUrl: '',
    businessName: '',
    whatsappNumber: '',
    category: '',
    description: '',
    businessAddress: '',
    city: '',
    state: '',
    storeLogo: null
  });

  const [productData, setProductData] = useState({
    name: '',
    price: '',
    discountPrice: '',
    quantity: '',
    description: '',
    image: null
  });

  const [paymentData, setPaymentData] = useState({
    accountNumber: '',
    bankCode: '',
    bankName: '',
    accountName: ''
  });

  // localStorage draft key, namespaced per user. We rehydrate text fields on
  // mount (File objects are intentionally skipped — they can't be serialized
  // and the merchant can re-pick the image quickly).
  const draftKey = user?.id ? `revstar:onboarding-draft:${user.id}` : null;
  const draftHydratedRef = useRef(false);

  const banks = [
    { name: 'Access Bank', code: '044' },
    { name: 'GTBank (Guaranty Trust Bank)', code: '058' },
    { name: 'First Bank of Nigeria', code: '011' },
    { name: 'Zenith Bank', code: '057' },
    { name: 'United Bank for Africa (UBA)', code: '033' },
    { name: 'Ecobank Nigeria', code: '050' },
    { name: 'FCMB (First City Monument Bank)', code: '214' },
    { name: 'Fidelity Bank', code: '070' },
    { name: 'Sterling Bank', code: '232' },
    { name: 'Stanbic IBTC Bank', code: '221' },
    { name: 'Union Bank of Nigeria', code: '032' },
    { name: 'Unity Bank', code: '215' },
    { name: 'Wema Bank', code: '035' },
    { name: 'Kuda Bank', code: '50211' },
    { name: 'OPay', code: '999992' },
    { name: 'PalmPay', code: '999991' },
    { name: 'Moniepoint', code: '50515' },
    { name: 'Carbon', code: '565' },
    { name: 'VFD Microfinance Bank', code: '566' },
    { name: 'Citibank Nigeria', code: '023' },
    { name: 'Heritage Bank', code: '030' },
    { name: 'Keystone Bank', code: '082' },
    { name: 'Polaris Bank', code: '076' },
    { name: 'Providus Bank', code: '101' },
    { name: 'Standard Chartered Bank', code: '068' },
    { name: 'Globus Bank', code: '00103' },
    { name: 'Titan Trust Bank', code: '102' },
    { name: 'Jaiz Bank', code: '301' },
    { name: 'TAJ Bank', code: '302' },
    { name: 'Lotus Bank', code: '303' },
    { name: 'Premium Trust Bank', code: '105' },
    { name: 'SunTrust Bank', code: '100' },
    { name: 'Parallex Bank', code: '104' },
    { name: 'Rubies Bank', code: '125' },
    { name: 'Sparkle Microfinance Bank', code: '51310' },
    { name: 'LAPO Microfinance Bank', code: '51211' },
    { name: 'Coronation Merchant Bank', code: '559' }
  ];

  const [verifying, setVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [accountNameVerified, setAccountNameVerified] = useState(false);
  const [discountPriceError, setDiscountPriceError] = useState('');

  // Theme step (O2). Themes load lazily when entering Step 4 to keep the
  // initial wizard render fast. selectedThemeId defaults to the built-in
  // DEFAULT_THEME_ID so the "Default theme" tile renders pre-selected;
  // activation is skipped for that sentinel (see the launch handler).
  const [themes, setThemes] = useState([]);
  const [themesLoading, setThemesLoading] = useState(false);
  const [themesLoaded, setThemesLoaded] = useState(false);
  const [selectedThemeId, setSelectedThemeId] = useState(DEFAULT_THEME_ID);

  const steps = [
    { id: 1, title: 'Store',   icon: Store },
    { id: 2, title: 'Product', icon: Package },
    { id: 3, title: 'Payment', icon: CreditCard },
    { id: 4, title: 'Theme',   icon: Palette },
    { id: 5, title: 'Launch',  icon: Rocket }
  ];

  // Auth guards
  useEffect(() => {
    if (!authLoading) {
      if (!isAuthenticated) {
        const returnUrl = encodeURIComponent('/onboarding');
        navigate(`/login?redirect=${returnUrl}`, { replace: true });
      } else if (user?.onboardingCompleted) {
        toast.info('You have already completed onboarding');
        navigate('/overview', { replace: true });
      }
    }
  }, [isAuthenticated, authLoading, user, navigate, toast]);

  // Auto-verify bank when account number reaches 10 digits + bank picked
  useEffect(() => {
    if (
      paymentData.accountNumber &&
      paymentData.bankCode &&
      paymentData.accountNumber.length === 10 &&
      !accountNameVerified &&
      !verifying
    ) {
      verifyBankAccount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentData.accountNumber, paymentData.bankCode]);

  // Hydrate from localStorage once the user id is known. We mark the slug as
  // user-edited if a draft slug was present, so resuming a draft doesn't
  // suddenly clobber it from the business name.
  useEffect(() => {
    if (!draftKey || draftHydratedRef.current) return;
    draftHydratedRef.current = true;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft.storeData) {
        setStoreData(prev => ({ ...prev, ...draft.storeData, storeLogo: null }));
        if (draft.storeData.storeUrl) slugAutoSyncRef.current = false;
      }
      if (draft.productData) {
        setProductData(prev => ({ ...prev, ...draft.productData, image: null }));
      }
      if (draft.paymentData) {
        setPaymentData(prev => ({ ...prev, ...draft.paymentData }));
      }
      if (typeof draft.currentStep === 'number') {
        setCurrentStep(Math.min(Math.max(draft.currentStep, 1), 4));
      }
    } catch (err) {
      console.warn('[Onboarding] Failed to load draft:', err);
    }
  }, [draftKey]);

  // Lazy-fetch themes when the merchant first reaches Step 4. We sort with a
  // priority key so industry-matching free themes appear first, then other
  // free themes, then premium ones (rendered as locked cards). Premium are
  // included so merchants see what's available — they just can't activate
  // until they upgrade. Auto-skip the step if the marketplace returns nothing
  // (e.g., seed not run on this env).
  useEffect(() => {
    if (currentStep !== 4 || themesLoaded || themesLoading) return;
    let cancelled = false;
    (async () => {
      setThemesLoading(true);
      try {
        const response = await axios.get('/themes');
        if (cancelled) return;
        const list = Array.isArray(response.data?.themes) ? response.data.themes : [];
        const matchIndustry = categoryToIndustry(storeData.category);
        const sorted = [...list].sort((a, b) => {
          // Priority key (lower = earlier). 0 = industry-match free,
          // 1 = other free, 2 = premium. Within tier, defer to the API's
          // existing sortOrder/featured ordering.
          const score = (t) => {
            const isFree = !t.isPremium && (t.price ?? 0) === 0;
            if (isFree && matchIndustry && t.industry === matchIndustry) return 0;
            if (isFree) return 1;
            return 2;
          };
          return score(a) - score(b);
        });
        setThemes(sorted);
        setThemesLoaded(true);
        // Note: the previous no-free auto-skip is gone now that the
        // built-in Default theme is always present as the first
        // tile. Even a premium-only marketplace leaves the merchant
        // with a valid pick on this step.
      } catch (err) {
        if (cancelled) return;
        console.error('[Onboarding] Failed to load themes:', err);
        // On fetch error, fall through to Launch — don't strand the merchant.
        setThemesLoaded(true);
        setCurrentStep(5);
      } finally {
        if (!cancelled) setThemesLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // storeData.category included so re-entering with a different category
    // re-sorts. themesLoaded gate prevents refetch loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // Persist text fields whenever they change. Files are skipped — they can't
  // be JSON-serialized and the merchant can re-pick them in seconds.
  useEffect(() => {
    if (!draftKey || !draftHydratedRef.current) return;
    try {
      const { storeLogo, ...storeRest } = storeData;
      const { image, ...productRest } = productData;
      localStorage.setItem(draftKey, JSON.stringify({
        currentStep,
        storeData: storeRest,
        productData: productRest,
        paymentData
      }));
    } catch (err) {
      // Quota exceeded or storage disabled — non-fatal.
      console.warn('[Onboarding] Failed to save draft:', err);
    }
  }, [draftKey, currentStep, storeData, productData, paymentData]);

  // ─── Slug check ──────────────────────────────────────────────────────
  const checkSlugAvailability = async (slug) => {
    if (!slug || slug.length < 3) {
      setSlugAvailable(null);
      setSlugError(slug && slug.length < 3 ? 'Slug must be at least 3 characters' : '');
      return;
    }
    setSlugCheckLoading(true);
    try {
      const response = await axios.get(`/stores/check-slug-public?slug=${encodeURIComponent(slug)}`);
      if (response.data.available) {
        setSlugAvailable(true);
        setSlugError('');
      } else {
        setSlugAvailable(false);
        setSlugError(response.data.error || 'This URL is already taken');
      }
    } catch (error) {
      console.error('Slug check error:', error);
      setSlugAvailable(null);
      setSlugError('Unable to verify availability');
    } finally {
      setSlugCheckLoading(false);
    }
  };

  const triggerSlugCheck = (slug) => {
    setSlugAvailable(null);
    setSlugError('');
    if (slugCheckTimeoutRef.current) clearTimeout(slugCheckTimeoutRef.current);
    if (slug) {
      slugCheckTimeoutRef.current = setTimeout(() => checkSlugAvailability(slug), 500);
    }
  };

  // ─── Field handlers ──────────────────────────────────────────────────
  const handleStoreChange = (field, value) => {
    if (field === 'businessName') {
      const nextStore = { ...storeData, businessName: value };
      // Auto-sync slug from business name UNTIL the user manually edits the slug field
      if (slugAutoSyncRef.current) {
        const suggested = slugFromName(value);
        nextStore.storeUrl = suggested;
        triggerSlugCheck(suggested);
      }
      setStoreData(nextStore);
      return;
    }

    if (field === 'storeUrl') {
      // Cleanse to lowercase + alphanumeric + hyphens only
      const cleaned = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
      // Once the user types in the slug field, stop auto-syncing from business name
      slugAutoSyncRef.current = false;
      setStoreData({ ...storeData, storeUrl: cleaned });
      triggerSlugCheck(cleaned);
      return;
    }

    setStoreData({ ...storeData, [field]: value });
  };

  const handleProductChange = (field, value) => {
    const newData = { ...productData, [field]: value };
    setProductData(newData);
    if (field === 'discountPrice' || field === 'price') {
      const price = field === 'price' ? parseFloat(value) : parseFloat(productData.price);
      const discountPrice = field === 'discountPrice' ? parseFloat(value) : parseFloat(productData.discountPrice);
      if (discountPrice && price && discountPrice >= price) {
        setDiscountPriceError('Discount price must be less than the regular price');
      } else {
        setDiscountPriceError('');
      }
    }
  };

  const handlePaymentChange = (field, value) => {
    if (field === 'bankCode') {
      const selectedBank = banks.find(b => b.code === value);
      setPaymentData({
        ...paymentData,
        bankCode: value,
        bankName: selectedBank ? selectedBank.name : ''
      });
    } else {
      setPaymentData({ ...paymentData, [field]: value });
    }
    if (field === 'accountNumber' || field === 'bankCode') {
      setVerificationStatus(null);
      setVerificationMessage('');
      setAccountNameVerified(false);
    }
  };

  const verifyBankAccount = async () => {
    const { accountNumber, bankCode, bankName } = paymentData;
    if (!accountNumber || !bankCode || accountNumber.length !== 10) return;
    setVerifying(true);
    setVerificationStatus(null);
    setVerificationMessage('');
    try {
      const response = await axios.post('/kyc/verify-bank', { accountNumber, bankCode, bankName });
      if (response.data.success && response.data.data?.accountName) {
        setPaymentData({ ...paymentData, accountName: response.data.data.accountName });
        setAccountNameVerified(true);
        setVerificationStatus('success');
        setVerificationMessage(`Account verified: ${response.data.data.accountName}`);
      } else {
        setVerificationStatus('error');
        setVerificationMessage(response.data.error || 'Unable to verify account. Please check your details.');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setVerificationStatus('error');
      setVerificationMessage(error.response?.data?.error || 'Unable to verify account. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) setProductData({ ...productData, image: file });
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Soft client-side limit. Backend enforces 5MB hard cap via multer.
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo is too large. Max 5MB.');
      return;
    }
    setStoreData(prev => ({ ...prev, storeLogo: file }));
  };

  const removeLogo = () => {
    setStoreData(prev => ({ ...prev, storeLogo: null }));
  };

  // ─── AI generation ───────────────────────────────────────────────────
  const generateStoreDescription = async () => {
    if (!storeData.businessName || !storeData.category) {
      toast.warning('Add your business name and category first');
      return;
    }
    setGeneratingDescription(true);
    try {
      const response = await axios.post('/ai/merchant/generate-store-description', {
        storeName: storeData.businessName,
        businessType: storeData.category
      });
      if (response.data.success && response.data.description) {
        setStoreData(prev => ({ ...prev, description: response.data.description }));
        toast.success('Description generated');
      } else {
        toast.error(response.data.error || 'Failed to generate description');
      }
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error('Failed to generate description. Please try again.');
    } finally {
      setGeneratingDescription(false);
    }
  };

  const generateProductDescription = async () => {
    if (!productData.name) {
      toast.warning('Add a product name first');
      return;
    }
    setGeneratingProductDescription(true);
    try {
      const response = await axios.post('/ai/generate-description-public', {
        productName: productData.name,
        category: storeData.category || 'general'
      });
      if (response.data.success && response.data.description) {
        setProductData(prev => ({ ...prev, description: response.data.description }));
        toast.success('Description generated');
      } else {
        toast.error(response.data.error || 'Failed to generate description');
      }
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error('Failed to generate description. Please try again.');
    } finally {
      setGeneratingProductDescription(false);
    }
  };

  const generateStoreLogo = async () => {
    if (!storeData.businessName) {
      toast.warning('Add your business name first');
      return;
    }
    setGeneratingLogo(true);
    try {
      const response = await axios.post('/ai/merchant/generate-logo', {
        storeName: storeData.businessName,
        category: storeData.category
      });
      if (response.data.success && response.data.imageUrl) {
        // Convert the data URL to a File so it slots into the existing
        // storeLogo flow (multipart upload on launch).
        const dataUrl = response.data.imageUrl;
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        const safe = storeData.businessName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'logo';
        const file = new File([blob], `${safe}-logo.png`, { type: 'image/png' });
        setStoreData(prev => ({ ...prev, storeLogo: file }));
        toast.success('Logo generated — regenerate or upload your own anytime.');
      } else {
        toast.error(response.data.error || 'Failed to generate logo');
      }
    } catch (error) {
      console.error('AI logo generation error:', error);
      if (error.response?.status === 429) {
        toast.error(error.response?.data?.error || 'Too many tries. Please wait a bit before regenerating.');
      } else if (error.response?.status === 503) {
        toast.error('Logo generation is temporarily unavailable.');
      } else {
        toast.error(error.response?.data?.error || 'Failed to generate logo. Please try again.');
      }
    } finally {
      setGeneratingLogo(false);
    }
  };

  const generateProductImage = async () => {
    if (!productData.name) {
      toast.warning('Add a product name first');
      return;
    }
    setGeneratingProductImage(true);
    try {
      const response = await axios.post('/ai/generate-image-public', {
        productName: productData.name,
        category: storeData.category,
        description: productData.description
      });
      if (response.data.success && response.data.imageUrl) {
        const dataUrl = response.data.imageUrl;
        const res = await fetch(dataUrl);
        const blob = await res.blob();
        const file = new File([blob], `${productData.name.replace(/\s+/g, '-')}-ai.png`, { type: 'image/png' });
        setProductData(prev => ({ ...prev, image: file }));
        toast.success('Product image generated');
      } else {
        toast.error(response.data.error || 'Failed to generate image');
      }
    } catch (error) {
      console.error('AI image generation error:', error);
      if (error.response?.status === 429) {
        toast.error('Rate limit exceeded. Please try again later.');
      } else {
        toast.error('Failed to generate image. Please try again.');
      }
    } finally {
      setGeneratingProductImage(false);
    }
  };

  // ─── Step navigation ─────────────────────────────────────────────────
  // NOTE: saveStepData is currently a no-op. Real per-step persistence is
  // scheduled for Phase O1b alongside the new /api/merchant/onboarding/step/{n}
  // endpoint. Keeping the call site here so O1b is a small surgical change.
  const saveStepData = async () => Promise.resolve();

  const handleNext = async () => {
    if (currentStep < 5) {
      setLoading(true);
      await saveStepData();
      setLoading(false);
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleLaunch = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      // Strip the File from the JSON payload — it travels separately as
      // `storeLogo` multipart, and JSON.stringify would otherwise emit `{}`.
      const { storeLogo, ...storePayload } = storeData;
      formData.append('store', JSON.stringify(storePayload));
      const productPayload = {
        name: productData.name,
        price: productData.price,
        discountPrice: productData.discountPrice,
        quantity: productData.quantity,
        description: productData.description
      };
      formData.append('product', JSON.stringify(productPayload));
      formData.append('payment', JSON.stringify(paymentData));
      if (productData.image) formData.append('productImage', productData.image);
      if (storeLogo) formData.append('storeLogo', storeLogo);

      const response = await axios.post('/merchant/onboarding/complete', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        // Activate the chosen theme AFTER store creation. We split this from
        // /onboarding/complete on purpose: the activate route already creates
        // StoreThemeConfig + seeds page configs, and a theme-activation
        // failure should never roll back a successful onboarding. Worst case,
        // the merchant lands on the dashboard with the default theme and can
        // pick again from /dashboard/themes.
        // The DEFAULT_THEME_ID sentinel means "keep the built-in
        // default" — no activation request needed (the absence of a
        // ThemeConfig row IS the default state).
        if (selectedThemeId && selectedThemeId !== DEFAULT_THEME_ID) {
          try {
            await axios.post(`/themes/${selectedThemeId}/activate`);
          } catch (themeErr) {
            console.error('[Onboarding] Theme activation failed:', themeErr);
            toast.warning("Store launched, but we couldn't apply your theme. You can pick one from the dashboard.");
          }
        }

        // Clear draft — onboarding is done.
        if (draftKey) {
          try { localStorage.removeItem(draftKey); } catch (_) { /* non-fatal */ }
        }
        toast.success('Store created successfully!');
        // /overview is the new post-onboarding landing — welcome + KYC + setup
        // checklist. /dashboard is now KPIs-only and not the right surface for
        // a brand-new merchant with no orders yet.
        window.location.href = '/overview';
        return;
      }
      toast.error(response.data.error || 'Failed to launch store');
    } catch (error) {
      console.error('Launch error:', error);
      // Surface the real backend message when there is one — previously this
      // caught block always showed 'Network error', hiding genuine 500s
      // (unique-constraint clashes, S3 failures, schema mismatches, etc.).
      // True network failures don't include err.response, so fall back to
      // the generic message only in that case.
      const serverMessage = error?.response?.data?.error;
      if (serverMessage) {
        toast.error(serverMessage);
      } else if (error?.response?.status) {
        toast.error(`Couldn't launch store (HTTP ${error.response.status}). Please try again.`);
      } else {
        toast.error('Network error. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return (
          storeData.storeUrl &&
          storeData.businessName &&
          storeData.category &&
          slugAvailable === true
        );
      case 2:
        return productData.name && productData.price && productData.quantity && !discountPriceError;
      case 3:
        return paymentData.accountNumber && paymentData.bankName && paymentData.accountName;
      case 4:
        // Theme is optional — null means "use default", a valid choice.
        return true;
      default:
        return true;
    }
  };

  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to logout. Please try again.');
    } finally {
      setLoggingOut(false);
    }
  };

  // ─── Render guards ───────────────────────────────────────────────────
  if (authLoading) {
    return (
      <div className="ob-auth-loading">
        <Loader2 size={32} className="ob-spin" />
        <p>Loading…</p>
      </div>
    );
  }
  if (!isAuthenticated || user?.onboardingCompleted) return null;

  const slugGibberishWarning = looksLikeGibberish(storeData.storeUrl);
  const progressPercent = Math.min(100, Math.round((currentStep / steps.length) * 100));

  // ─── Render ──────────────────────────────────────────────────────────
  return (
    <div className="ob-container">
      <div className="ob-topbar">
        <img src="/logo192.png" alt="Revstar" className="ob-brand-logo" />
        <button
          type="button"
          className="ob-logout"
          onClick={handleLogout}
          disabled={loggingOut}
          aria-label="Sign out"
        >
          {loggingOut ? <Loader2 size={16} className="ob-spin" /> : <Power size={16} />}
          <span className="ob-logout-label">Sign out</span>
        </button>
      </div>

      <main className="ob-content">
        <header className="ob-header">
          <h1 className="ob-title">Let's set up your store</h1>
          <p className="ob-subtitle">Complete these steps to get your store live.</p>
          <div className="ob-progress" aria-label={`Step ${currentStep} of ${steps.length}`}>
            <div className="ob-progress-bar" style={{ width: `${progressPercent}%` }} />
          </div>
          <div className="ob-progress-meta">
            <span>Step {currentStep} of {steps.length}</span>
            <span>{steps[currentStep - 1]?.title}</span>
          </div>
        </header>

        <section className="ob-step">
          {currentStep === 1 && (
            <>
              <h2 className="ob-step-title">Tell us about your store</h2>
              <p className="ob-step-subtitle">The basics — you can edit any of this later in Settings.</p>

              <div className="ob-field ob-field-logo">
                <label className="ob-label">Store logo <span className="ob-label-optional">(optional)</span></label>
                <div className="ob-logo-uploader">
                  <input
                    type="file"
                    id="ob-store-logo"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    style={{ display: 'none' }}
                  />
                  {/* Wrap is a sibling of the AI button so clicks on the
                      sparkle don't bubble to the file-picker label. */}
                  <div className="ob-logo-avatar-wrap">
                    <label htmlFor="ob-store-logo" className="ob-logo-avatar" aria-label="Upload store logo">
                      {generatingLogo ? (
                        <Loader2 size={28} className="ob-spin" />
                      ) : storeData.storeLogo ? (
                        <img
                          src={URL.createObjectURL(storeData.storeLogo)}
                          alt="Store logo preview"
                          className="ob-logo-image"
                        />
                      ) : (
                        <ImageIcon size={28} strokeWidth={1.5} />
                      )}
                    </label>
                    <button
                      type="button"
                      className="ob-logo-ai"
                      onClick={generateStoreLogo}
                      disabled={!storeData.businessName || generatingLogo}
                      title={
                        !storeData.businessName
                          ? 'Add your business name first'
                          : storeData.storeLogo
                          ? 'Regenerate logo with AI'
                          : 'Generate logo with AI'
                      }
                      aria-label="Generate logo with AI"
                    >
                      {generatingLogo ? <Loader2 size={14} className="ob-spin" /> : <Sparkles size={14} />}
                    </button>
                  </div>
                  <div className="ob-logo-meta">
                    <label htmlFor="ob-store-logo" className="ob-logo-action">
                      {storeData.storeLogo ? 'Replace' : 'Upload logo'}
                    </label>
                    {storeData.storeLogo && (
                      <button type="button" className="ob-logo-remove" onClick={removeLogo}>
                        Remove
                      </button>
                    )}
                    <span className="ob-hint">
                      {storeData.businessName
                        ? storeData.category
                          ? 'Tap the ✨ to generate, or upload your own · Square works best · Max 5MB'
                          : 'Tap the ✨ to generate · Picking a category gives sharper results · Max 5MB'
                        : 'Add your business name to use AI · Square works best · Max 5MB'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="ob-field">
                <label htmlFor="ob-business-name" className="ob-label">Business name</label>
                <input
                  id="ob-business-name"
                  type="text"
                  className="ob-input"
                  placeholder="e.g. Gbotemi Stores"
                  value={storeData.businessName}
                  onChange={(e) => handleStoreChange('businessName', e.target.value)}
                />
              </div>

              <div className="ob-field">
                <label htmlFor="ob-store-url" className="ob-label">Store URL</label>
                <div className={`ob-url-input ${slugAvailable === true ? 'ob-url-valid' : slugAvailable === false ? 'ob-url-invalid' : ''}`}>
                  <input
                    id="ob-store-url"
                    type="text"
                    placeholder="your-store-name"
                    value={storeData.storeUrl}
                    onChange={(e) => handleStoreChange('storeUrl', e.target.value)}
                  />
                  <span className="ob-url-suffix">.{platformDomain}</span>
                  <span className="ob-url-status">
                    {slugCheckLoading && <Loader2 size={16} className="ob-spin" />}
                    {!slugCheckLoading && slugAvailable === true && <CheckCircle size={16} className="ob-status-ok" />}
                    {!slugCheckLoading && slugAvailable === false && <AlertCircle size={16} className="ob-status-bad" />}
                  </span>
                </div>
                {slugError && <span className="ob-field-error">{slugError}</span>}
                {!slugError && !slugCheckLoading && slugAvailable === true && (
                  <span className="ob-field-success">This URL is available.</span>
                )}
                {slugGibberishWarning && slugAvailable !== false && (
                  <div className="ob-field-warning">
                    <AlertTriangle size={14} />
                    <span>This may be hard for customers to remember. Try using your business name.</span>
                  </div>
                )}
              </div>

              <div className="ob-field">
                <label htmlFor="ob-whatsapp" className="ob-label">WhatsApp number</label>
                <input
                  id="ob-whatsapp"
                  type="tel"
                  className="ob-input"
                  placeholder="+234 XXX XXX XXXX"
                  value={storeData.whatsappNumber}
                  onChange={(e) => handleStoreChange('whatsappNumber', e.target.value)}
                />
              </div>

              <div className="ob-field">
                <label htmlFor="ob-business-address" className="ob-label">
                  <MapPin size={14} className="ob-label-icon" />
                  Business address <span className="ob-label-optional">(optional)</span>
                </label>
                <input
                  id="ob-business-address"
                  type="text"
                  className="ob-input"
                  placeholder="Street address"
                  value={storeData.businessAddress}
                  onChange={(e) => handleStoreChange('businessAddress', e.target.value)}
                />
                <span className="ob-hint">Used for shipping origin and KYC verification.</span>
              </div>

              <div className="ob-field-row">
                <div className="ob-field">
                  <label htmlFor="ob-city" className="ob-label">City</label>
                  <input
                    id="ob-city"
                    type="text"
                    className="ob-input"
                    placeholder="e.g. Ikeja"
                    value={storeData.city}
                    onChange={(e) => handleStoreChange('city', e.target.value)}
                  />
                </div>
                <div className="ob-field">
                  <label htmlFor="ob-state" className="ob-label">State</label>
                  <input
                    id="ob-state"
                    type="text"
                    className="ob-input"
                    placeholder="e.g. Lagos"
                    value={storeData.state}
                    onChange={(e) => handleStoreChange('state', e.target.value)}
                  />
                </div>
              </div>

              <div className="ob-field">
                <label htmlFor="ob-category" className="ob-label">Category</label>
                <select
                  id="ob-category"
                  className="ob-input"
                  value={storeData.category}
                  onChange={(e) => handleStoreChange('category', e.target.value)}
                >
                  <option value="">Select a category</option>
                  <option value="fashion">Fashion & Apparel</option>
                  <option value="electronics">Electronics</option>
                  <option value="food">Food & Beverages</option>
                  <option value="beauty">Beauty & Cosmetics</option>
                  <option value="home">Home & Living</option>
                  <option value="sports">Sports & Fitness</option>
                  <option value="books">Books & Media</option>
                  <option value="toys">Toys & Games</option>
                  <option value="auto">Auto & Vehicles</option>
                  <option value="realestate">Real Estate & Property</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="ob-field">
                <label htmlFor="ob-description" className="ob-label">Store description</label>
                <div className="ob-textarea-wrap">
                  <textarea
                    id="ob-description"
                    className="ob-textarea"
                    rows="4"
                    placeholder="Describe what you sell…"
                    value={storeData.description}
                    onChange={(e) => handleStoreChange('description', e.target.value)}
                  />
                  <button
                    type="button"
                    className="ob-ai-cta"
                    onClick={generateStoreDescription}
                    disabled={!storeData.businessName || !storeData.category || generatingDescription}
                    title="Generate with AI"
                    aria-label="Generate description with AI"
                  >
                    {generatingDescription ? <Loader2 size={14} className="ob-spin" /> : <Sparkles size={14} />}
                  </button>
                </div>
                {(!storeData.businessName || !storeData.category) && (
                  <span className="ob-hint">Fill in your business name and category to use AI generation.</span>
                )}
              </div>
            </>
          )}

          {currentStep === 2 && (
            <>
              <h2 className="ob-step-title">Add your first product</h2>
              <p className="ob-step-subtitle">You can add more products later from the dashboard.</p>

              <div className="ob-field">
                <label className="ob-label">Product image</label>
                <div className="ob-image-field">
                  <input
                    type="file"
                    id="ob-product-image"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="ob-product-image" className="ob-image-drop">
                    {productData.image ? (
                      <img
                        src={URL.createObjectURL(productData.image)}
                        alt="Product preview"
                        className="ob-image-preview"
                      />
                    ) : (
                      <div className="ob-image-placeholder">
                        <Upload size={24} />
                        <span>Upload product image</span>
                      </div>
                    )}
                  </label>
                  {productData.image && (
                    <button
                      type="button"
                      className="ob-image-remove"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setProductData(prev => ({ ...prev, image: null }));
                      }}
                      title="Remove image"
                      aria-label="Remove image"
                    >
                      <X size={14} strokeWidth={2.5} />
                    </button>
                  )}
                  <button
                    type="button"
                    className="ob-ai-cta"
                    onClick={(e) => { e.stopPropagation(); generateProductImage(); }}
                    disabled={!productData.name || generatingProductImage}
                    title="Generate product image with AI"
                    aria-label="Generate product image with AI"
                  >
                    {generatingProductImage ? <Loader2 size={14} className="ob-spin" /> : <Sparkles size={14} />}
                  </button>
                </div>
                <span className="ob-hint">
                  {productData.image
                    ? 'Tap the image to replace · Max 5MB · JPG, PNG, WEBP'
                    : 'Max 5MB · JPG, PNG, WEBP'}
                </span>
              </div>

              <div className="ob-field">
                <label htmlFor="ob-product-name" className="ob-label">Product name</label>
                <input
                  id="ob-product-name"
                  type="text"
                  className="ob-input"
                  placeholder="e.g. Premium Cotton T-Shirt"
                  value={productData.name}
                  onChange={(e) => handleProductChange('name', e.target.value)}
                />
              </div>

              <div className="ob-field-row">
                <div className="ob-field">
                  <label htmlFor="ob-product-price" className="ob-label">Price (₦)</label>
                  <input
                    id="ob-product-price"
                    type="number"
                    className="ob-input"
                    placeholder="5000"
                    value={productData.price}
                    onChange={(e) => handleProductChange('price', e.target.value)}
                  />
                </div>
                <div className="ob-field">
                  <label htmlFor="ob-product-discount" className="ob-label">Discount price (₦)</label>
                  <input
                    id="ob-product-discount"
                    type="number"
                    className={`ob-input ${discountPriceError ? 'ob-input-error' : ''}`}
                    placeholder="4500"
                    value={productData.discountPrice}
                    onChange={(e) => handleProductChange('discountPrice', e.target.value)}
                  />
                  {discountPriceError && <span className="ob-field-error">{discountPriceError}</span>}
                </div>
              </div>

              <div className="ob-field">
                <label htmlFor="ob-product-qty" className="ob-label">Quantity in stock</label>
                <input
                  id="ob-product-qty"
                  type="number"
                  className="ob-input"
                  placeholder="100"
                  value={productData.quantity}
                  onChange={(e) => handleProductChange('quantity', e.target.value)}
                />
              </div>

              <div className="ob-field">
                <label htmlFor="ob-product-description" className="ob-label">Product description</label>
                <div className="ob-textarea-wrap">
                  <textarea
                    id="ob-product-description"
                    className="ob-textarea"
                    rows="4"
                    placeholder="Describe features, materials, sizes…"
                    value={productData.description}
                    onChange={(e) => handleProductChange('description', e.target.value)}
                  />
                  <button
                    type="button"
                    className="ob-ai-cta"
                    onClick={generateProductDescription}
                    disabled={!productData.name || generatingProductDescription}
                    title="Generate with AI"
                    aria-label="Generate product description with AI"
                  >
                    {generatingProductDescription ? <Loader2 size={14} className="ob-spin" /> : <Sparkles size={14} />}
                  </button>
                </div>
                {!productData.name && (
                  <span className="ob-hint">Add a product name to use AI generation.</span>
                )}
              </div>
            </>
          )}

          {currentStep === 3 && (
            <>
              <h2 className="ob-step-title">Set up payments</h2>
              <p className="ob-step-subtitle">We'll send your settlements to this account.</p>

              <div className="ob-field">
                <label htmlFor="ob-acct-number" className="ob-label">Account number</label>
                <input
                  id="ob-acct-number"
                  type="text"
                  className="ob-input"
                  placeholder="1234567890"
                  maxLength="10"
                  value={paymentData.accountNumber}
                  onChange={(e) => handlePaymentChange('accountNumber', e.target.value.replace(/\D/g, ''))}
                />
                <span className="ob-hint">10-digit Nigerian account number.</span>
              </div>

              <div className="ob-field">
                <label htmlFor="ob-bank" className="ob-label">Bank</label>
                <select
                  id="ob-bank"
                  className="ob-input"
                  value={paymentData.bankCode}
                  onChange={(e) => handlePaymentChange('bankCode', e.target.value)}
                >
                  <option value="">Select your bank</option>
                  {banks.map(bank => (
                    <option key={bank.code} value={bank.code}>{bank.name}</option>
                  ))}
                </select>
              </div>

              <div className="ob-field">
                <label htmlFor="ob-acct-name" className="ob-label">Account name</label>
                <div className="ob-input-with-status">
                  <input
                    id="ob-acct-name"
                    type="text"
                    className={`ob-input ${accountNameVerified ? 'ob-input-verified' : ''}`}
                    placeholder={verifying ? 'Verifying…' : 'Account name will be auto-filled'}
                    value={paymentData.accountName}
                    onChange={(e) => !accountNameVerified && handlePaymentChange('accountName', e.target.value)}
                    readOnly={accountNameVerified}
                  />
                  {verifying && <span className="ob-input-spinner"><Loader2 size={16} className="ob-spin" /></span>}
                </div>
                {verificationStatus === 'success' && (
                  <span className="ob-field-success">{verificationMessage}</span>
                )}
                {verificationStatus === 'error' && (
                  <span className="ob-field-error">{verificationMessage}</span>
                )}
                {!verificationStatus && !verifying && (
                  <span className="ob-hint">Fill in account number and bank to auto-verify.</span>
                )}
              </div>

              <div className="ob-info-card">
                <h4>Things to know</h4>
                <ul>
                  <li>Account name should match your business or registered name.</li>
                  <li>Settlements are processed within 24–48 hours.</li>
                  <li>You can update these details anytime from Settings.</li>
                </ul>
              </div>
            </>
          )}

          {currentStep === 4 && (
            <>
              <h2 className="ob-step-title">Pick a theme</h2>
              <p className="ob-step-subtitle">
                {categoryToIndustry(storeData.category)
                  ? "We've highlighted themes that suit your industry. You can change this anytime from your dashboard."
                  : "You can change this anytime from your dashboard."}
              </p>

              {themesLoading && (
                <div className="ob-themes-loading">
                  <Loader2 size={22} className="ob-spin" />
                  <span>Loading themes…</span>
                </div>
              )}

              {!themesLoading && themesLoaded && (
                <>
                  <div className="ob-theme-grid">
                    {/* Prepend the built-in default theme so it
                        always sits first. Marketplace themes follow
                        in the order returned by the sorter above. */}
                    {[DEFAULT_THEME, ...themes].map((theme) => {
                      const isDefaultTile = theme.id === DEFAULT_THEME_ID;
                      const isFree = !theme.isPremium && (theme.price ?? 0) === 0;
                      const isLocked = !isFree; // FREE-tier merchants can't activate paid themes here
                      const isSelected = selectedThemeId === theme.id;
                      const matches = !isDefaultTile && categoryToIndustry(storeData.category) === theme.industry;
                      const previewUrl = Array.isArray(theme.previewImages) && theme.previewImages.length > 0
                        ? theme.previewImages[0]
                        : null;

                      return (
                        <button
                          type="button"
                          key={theme.id}
                          className={`ob-theme-card${isSelected ? ' ob-theme-card-selected' : ''}${isLocked ? ' ob-theme-card-locked' : ''}${isDefaultTile ? ' ob-theme-card-default' : ''}`}
                          onClick={() => {
                            if (isLocked) return;
                            if (isDefaultTile) {
                              // Default tile can't be deselected —
                              // "no default" isn't a valid state.
                              setSelectedThemeId(DEFAULT_THEME_ID);
                              return;
                            }
                            // For marketplace tiles: clicking the
                            // currently-selected one returns to the
                            // built-in default rather than going to
                            // an unselected null state.
                            setSelectedThemeId(isSelected ? DEFAULT_THEME_ID : theme.id);
                          }}
                          disabled={isLocked}
                          aria-pressed={isSelected}
                          aria-label={isDefaultTile
                            ? 'Default theme — built-in storefront look (selected by default)'
                            : `${theme.name}${isFree ? ' — free' : ' — premium, requires upgrade'}`}
                        >
                          <div className="ob-theme-preview">
                            {isDefaultTile ? (
                              // Stylized mini-mockup of the default
                              // storefront layout — header pill +
                              // hero block + product grid stubs.
                              // Gives the tile a recognizable shape
                              // without needing a real preview image.
                              <div className="ob-theme-preview-default" aria-hidden="true">
                                <div className="ob-theme-preview-default-nav" />
                                <div className="ob-theme-preview-default-hero" />
                                <div className="ob-theme-preview-default-row">
                                  <div />
                                  <div />
                                  <div />
                                </div>
                              </div>
                            ) : previewUrl ? (
                              <img src={previewUrl} alt="" />
                            ) : (
                              <div className="ob-theme-preview-placeholder">
                                <Palette size={28} strokeWidth={1.5} />
                              </div>
                            )}
                            {isSelected && (
                              <span className="ob-theme-selected-badge" aria-hidden="true">
                                <Check size={14} strokeWidth={3} />
                              </span>
                            )}
                            {isLocked && (
                              <span className="ob-theme-locked-overlay">
                                <Lock size={14} />
                                <span>Upgrade to PRO</span>
                              </span>
                            )}
                          </div>
                          <div className="ob-theme-meta">
                            <div className="ob-theme-name-row">
                              <span className="ob-theme-name">{theme.name}</span>
                              {isFree ? (
                                <span className="ob-theme-badge ob-theme-badge-free">Free</span>
                              ) : (
                                <span className="ob-theme-badge ob-theme-badge-premium">PRO</span>
                              )}
                            </div>
                            {isDefaultTile && (
                              <span className="ob-theme-match">Selected by default</span>
                            )}
                            {matches && isFree && (
                              <span className="ob-theme-match">For your industry</span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <p className="ob-theme-hint">
                    {selectedThemeId === DEFAULT_THEME_ID
                      ? "We'll launch your store on our built-in default theme. You can switch to a marketplace theme anytime from your dashboard."
                      : "Tap Continue to apply this theme to your storefront on launch."}
                  </p>
                </>
              )}
            </>
          )}

          {currentStep === 5 && (
            <>
              <div className="ob-launch-hero">
                <div className="ob-launch-badge">
                  <Store size={36} strokeWidth={1.75} />
                  <span className="ob-launch-badge-check" aria-hidden="true">
                    <Check size={14} strokeWidth={3} />
                  </span>
                </div>
                <h2 className="ob-step-title">Your store is ready</h2>
                <p className="ob-step-subtitle">Quick review, then we'll bring it live.</p>
              </div>

              <div className="ob-review">
                <div className="ob-review-card">
                  <div className="ob-review-header">
                    <Store size={18} />
                    <h3>Store</h3>
                  </div>
                  <dl>
                    <div><dt>URL</dt><dd>{storeData.storeUrl}.{platformDomain}</dd></div>
                    <div><dt>Name</dt><dd>{storeData.businessName}</dd></div>
                    <div><dt>Category</dt><dd>{storeData.category || '—'}</dd></div>
                  </dl>
                </div>

                <div className="ob-review-card">
                  <div className="ob-review-header">
                    <Package size={18} />
                    <h3>First product</h3>
                  </div>
                  <dl>
                    <div><dt>Name</dt><dd>{productData.name}</dd></div>
                    <div><dt>Price</dt><dd>₦{productData.price}</dd></div>
                    <div><dt>Stock</dt><dd>{productData.quantity} units</dd></div>
                  </dl>
                </div>

                <div className="ob-review-card">
                  <div className="ob-review-header">
                    <CreditCard size={18} />
                    <h3>Payouts</h3>
                  </div>
                  <dl>
                    <div><dt>Bank</dt><dd>{paymentData.bankName}</dd></div>
                    <div><dt>Account</dt><dd>****{paymentData.accountNumber.slice(-4)}</dd></div>
                  </dl>
                </div>

                <div className="ob-review-card">
                  <div className="ob-review-header">
                    <Palette size={18} />
                    <h3>Theme</h3>
                  </div>
                  <dl>
                    <div>
                      <dt>Selected</dt>
                      <dd>
                        {selectedThemeId
                          ? (themes.find((t) => t.id === selectedThemeId)?.name || 'Custom')
                          : 'Default'}
                      </dd>
                    </div>
                  </dl>
                </div>

                <div className="ob-storefront-url">
                  <span className="ob-storefront-url-label">Your storefront</span>
                  <span className="ob-storefront-url-value">https://{storeData.storeUrl}.{platformDomain}</span>
                </div>
              </div>
            </>
          )}
        </section>
      </main>

      <nav className="ob-nav">
        {currentStep > 1 ? (
          <button
            type="button"
            className="ob-btn ob-btn-secondary"
            onClick={handleBack}
            disabled={loading}
          >
            <ArrowLeft size={18} />
            <span>Back</span>
          </button>
        ) : <span />}

        {currentStep < 5 ? (
          <button
            type="button"
            className="ob-btn ob-btn-primary"
            onClick={handleNext}
            disabled={!isStepValid() || loading}
          >
            <span>{loading ? 'Saving…' : 'Continue'}</span>
            <ArrowRight size={18} />
          </button>
        ) : (
          <button
            type="button"
            className="ob-btn ob-btn-primary"
            onClick={handleLaunch}
            disabled={loading}
          >
            <span>{loading ? 'Launching…' : 'Launch store'}</span>
            <Rocket size={18} />
          </button>
        )}
      </nav>
    </div>
  );
};

export default NewOnboardingWizard;
